/**
 * The one live region — `FE-04.11`, `entry-surfaces.md` D11.
 *
 * `announce(message)` is the only way anything in the product speaks to a
 * screen reader outside the natural document flow. The DOM node it writes
 * into is a **shell singleton**: exactly one
 * `<div aria-live="polite" aria-atomic="true">` exists, mounted once by
 * `RootLayout` (`apps/web/src/routes/__root.tsx`, via
 * `components/shell/LiveRegion.tsx`), and no other component may render a
 * second one — a second region is the classic bug D11 names: one of the two
 * silently stops being read.
 *
 * This module owns the *registration*, not the markup, on purpose: the live
 * region component registers its element here on mount and unregisters on
 * unmount, so `announce()` itself never touches React and stays trivial to
 * test without rendering anything.
 *
 * ⚠ **One event, one channel (D11).** A failed query or a sign-in failure is
 * announced through the `Notice`/`EmptyState` `role="alert"` it already
 * renders — `announce()` must NOT also be called for that event, or the
 * member hears it twice, with the assertive read interrupting the polite
 * one mid-sentence. `announce()` is for events with no `role="alert"`/
 * `role="status"` node of their own: a workspace was created, a slow fetch
 * finished, a debounced filter count settled.
 *
 * Never `assertive` — nothing this shell announces is an emergency, and an
 * assertive region interrupts a member mid-sentence (D11).
 */

/** The minimal surface `announce` needs — real usage passes a DOM element. */
interface LiveRegionTarget {
  textContent: string | null;
}

let region: LiveRegionTarget | null = null;
let pendingMessage: string | null = null;

/**
 * Speak `message` through the shell's live region.
 *
 * Safe to call before the shell has mounted (an optimistic update racing
 * the first paint): the most recent message is held and flushed as soon as
 * `registerLiveRegion` supplies the element.
 */
export function announce(message: string): void {
  if (region) {
    write(region, message);
    return;
  }
  pendingMessage = message;
}

/**
 * Registers (or, with `null`, unregisters) the DOM node `announce` writes
 * into. Called by `LiveRegion` alone — nothing else should call this.
 */
export function registerLiveRegion(target: LiveRegionTarget | null): void {
  region = target;
  if (target && pendingMessage !== null) {
    write(target, pendingMessage);
    pendingMessage = null;
  }
}

/**
 * Clears the region synchronously, then writes the message on the next
 * microtask. The clear-then-set split is what makes a screen reader
 * re-announce a message that happens to be identical to the region's
 * current text — an assistive technology only reacts to a *mutation*, and
 * setting the same string twice in one step is not one.
 */
function write(target: LiveRegionTarget, message: string): void {
  target.textContent = '';
  queueMicrotask(() => {
    target.textContent = message;
  });
}
